import { deletePasskeyApi, updatePasskeyApi, usePasskeys } from "@/api/services/main/auth";
import { TPasskeyItem } from "@/api/services/main/auth/dtos/passkey.dto";
import { AddPasskeyModal } from "@/components/commons/PasskeyManager/AddPasskeyModal";
import { DeletePasskeyModal } from "@/components/commons/PasskeyManager/DeletePasskeyModal";
import { PasskeyList } from "@/components/commons/PasskeyManager/PasskeyList";
import { RenamePasskeyModal } from "@/components/commons/PasskeyManager/RenamePasskeyModal";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { QUERY_KEYS } from "@/constants/queryKeys";
import {
  clearPasskeyOnDevice,
  isPasskeyAlreadyRegistered,
  isPasskeyCancellation,
  isPasskeySupported,
  markPasskeyOnDevice,
  registerPasskey,
} from "@/features/auth/utils/passkey";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import KeyIcon from "@mui/icons-material/Key";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";

export type TPasskeyListRenderProps = {
  passkeys: TPasskeyItem[];
  isLoading: boolean;
  onRename: (passkey: TPasskeyItem) => void;
  onDelete: (passkey: TPasskeyItem) => void;
  readOnly?: boolean;
};

type TPasskeyManagerProps = {
  embedded?: boolean;
  // Display-only: hides add and disables rename/delete (used on the edit screen; manage from Detail).
  readOnly?: boolean;
  renderList?: (props: TPasskeyListRenderProps) => ReactNode;
};

export const PasskeyManager: FC<TPasskeyManagerProps> = ({ embedded = false, readOnly = false, renderList }) => {
  const queryClient = useQueryClient();
  const passkeySupported = useMemo(() => isPasskeySupported(), []);
  const { data, isLoading } = usePasskeys();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<TPasskeyItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [renameTarget, setRenameTarget] = useState<TPasskeyItem | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const passkeys = data?.data ?? [];

  // Sync the device passkey hint with the authoritative server list.
  useEffect(() => {
    if (isLoading) return;
    if (passkeys.length > 0) markPasskeyOnDevice();
    else clearPasskeyOnDevice();
  }, [isLoading, passkeys.length]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PASSKEY.LIST] });

  const closeAddModal = () => {
    setIsAddOpen(false);
  };

  const handleRegister = async (deviceName: string) => {
    setIsRegistering(true);
    try {
      await registerPasskey(deviceName || undefined);
      toast.success("パスキーを登録しました。");
      closeAddModal();
      invalidate();
    } catch (error) {
      if (isPasskeyCancellation(error)) return;
      if (isPasskeyAlreadyRegistered(error)) {
        toast.error("このデバイスには既にパスキーが登録されています。");
        return;
      }
      showError(error);
    } finally {
      setIsRegistering(false);
    }
  };

  const openRename = (passkey: TPasskeyItem) => {
    setRenameTarget(passkey);
  };

  const closeRename = () => {
    setRenameTarget(null);
  };

  const handleRename = async (deviceName: string) => {
    if (!renameTarget) return;
    setIsRenaming(true);
    try {
      await updatePasskeyApi(renameTarget.id, { deviceName });
      toast.success("パスキーの名前を変更しました。");
      closeRename();
      invalidate();
    } catch (error) {
      showError(error);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deletePasskeyApi(deleteTarget.id);
      toast.success("パスキーを削除しました。");
      setDeleteTarget(null);
      invalidate();
    } catch (error) {
      showError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const inner = (
    <>
      <Stack gap="20px">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant={embedded ? "sub16Semi" : "head20Bold"} color={text.primary}>
            パスキー管理
          </Typography>
          {passkeySupported && !readOnly && (
            <Button size="sm" variant="outline" onClick={() => setIsAddOpen(true)}>
              <Stack direction="row" gap={1} alignItems="center">
                <KeyIcon sx={{ fontSize: 18 }} />
                <Typography variant="body14Semi">新規追加</Typography>
              </Stack>
            </Button>
          )}
        </Stack>

        {!passkeySupported && (
          <Typography variant="body14" color={text.secondary}>
            このブラウザはパスキーに対応していません。
          </Typography>
        )}

        {renderList ? (
          renderList({ passkeys, isLoading, onRename: openRename, onDelete: setDeleteTarget, readOnly })
        ) : (
          <PasskeyList passkeys={passkeys} isLoading={isLoading} onRename={openRename} onDelete={setDeleteTarget} />
        )}
      </Stack>

      <AddPasskeyModal isOpen={isAddOpen} isRegistering={isRegistering} onOk={handleRegister} onClose={closeAddModal} />

      <RenamePasskeyModal target={renameTarget} isSaving={isRenaming} onOk={handleRename} onClose={closeRename} />

      <DeletePasskeyModal target={deleteTarget} isDeleting={isDeleting} onOk={handleDelete} onClose={() => setDeleteTarget(null)} />
    </>
  );

  if (embedded) return inner;

  return <InnerBox padding="24px">{inner}</InnerBox>;
};
